import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useShortcuts from '../../hooks/useShortcuts'
import { WatchlistShortcutsKeyName } from './ShortcutsKeymap'
import { ShortcutsCategories, ShortcutType } from './Shortcuts.types'
import { fetchWatchlists } from '../../services/apis/watchlist'
import { UserWatchlists, Watchlist_WatchlistItem_Type } from '../../proto/generated/watchlist'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { saveSecurityExpression } from '../../services/apis/expression'
import { v4 as uuid } from 'uuid'
import { Security, SecurityExpression } from '../../proto/generated/security'
import { addOrUpdateUserExpression } from '../../reducer/security.reducer'
import commonActions from '../../actions/common.actions'
import { WidgetListItems } from '../../util/sidebar'
import { getExpressionFromSaved } from '../../util/expression'
import { selectCommonStore } from '../../selectors/common'
import { ChartContext } from '../chartV2/chart.context'
import { selectUserSecurityExpressions } from '../../selectors/security'
import { selectPrimarySecurity } from '../../selectors/home'
import ExpressionNameDialog from '../watchlist/expressionNameDialog'
import { setAddRemoveWatchlistItem } from '../../reducer/watchList.reducer'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import SectionNameDialog from '../watchlist/SectionNameDialog'
import { FeatureCategory, FeatureName } from '../adminUserRestrictions/Restrictions.constants'
import { setFeatureRestrictionDialog } from '../../reducer/user.reducer'
import { useUserOptional } from '../../context/UserContext'
import { FeatureCode } from '../../util/RestrictionFeatureCode'

const WatchlistShortcutRegister = () => {
    const dispatch = useDispatch()
    const [openSectionNameDialog, setOpenSectionNameDialog] = useState(false)
    const userWatchlists: UserWatchlists = useSelector((state: RootState) => state.watchListStore.userWatchlists)
    const { watchlistId: selectedWatchlistId, rightSideWidget } = useSelector(selectCommonStore)
    const currentSelectedWatchlist = userWatchlists?.watchlists.find(w => w.id === selectedWatchlistId)
    const chartContext = useContext(ChartContext)
    const { key, expressionMode, expressionSecurities } = useSelector(selectPrimarySecurity)
    const [primarySecurity, setPrimarySecurity] = useState<Security | undefined>()
    useEffect(() => { if (key) getSecurityAsync(key).then(setPrimarySecurity) }, [key])
    const securityKeyOrExpression = key
    const convertedExpression = expressionMode ? chartContext?.chart?.expression ?? '' : ''
    const watchlistedItem = currentSelectedWatchlist?.watchlistItems.find((item) => {
        return (expressionMode && item.type === Watchlist_WatchlistItem_Type.EXPRESSION && item.securityExpression && convertedExpression === item.securityExpression?.expression) ||
            (!expressionMode && item.type === Watchlist_WatchlistItem_Type.SECURITY && item.security && securityKeyOrExpression === getSecurityKey(item.security.exchange, item.security.code))
    })
    const alreadyAddedToWatchlist = useMemo(() => watchlistedItem !== undefined, [watchlistedItem])
    const [openExpressionNameDialog, setOpenExpressionNameDialog] = useState<boolean>(false)
    const savedExpressions: SecurityExpression[] | undefined = useSelector(selectUserSecurityExpressions)
    const userContext = useUserOptional()
    const hasAccess = useMemo(
        () => userContext?.user?.hasAccess ?? (() => true),
        [userContext]
    )

    const onSectionNameDialogClosed = useCallback((name: string | null) => {
        if (name) {
            onAddWatchlistSection(name)
        }
        setOpenSectionNameDialog(false)
    }, [])
    const addToWatchlist = useCallback(() => {
        if (!userWatchlists || userWatchlists.watchlists.length === 0) {
            fetchWatchlists({ dispatch: dispatch })
        }
        onAddOrRemovalFromWatchlist()
    }, [userWatchlists, dispatch, currentSelectedWatchlist, selectedWatchlistId, rightSideWidget, key, primarySecurity])

    const onExpressionNameDialogClosed = useCallback(
        (name: string | null) => {
            if (name !== null) {
                const securities = expressionSecurities.map((securityKey) => {
                    const exchangeAndCode = [securityKey.exchange, securityKey.code]
                    return {
                        exchange: exchangeAndCode[0],
                        code: exchangeAndCode[1]
                    }
                })

                saveSecurityExpression({
                    expression: {
                        id: uuid(),
                        expression: convertedExpression,
                        name: name,
                        securities: securities as Security[],
                        expired: false
                    }
                }).then((response) => {
                    dispatch(addOrUpdateUserExpression(response))
                    commonActions.deleteRecentExpression(dispatch, response.expression)
                    dispatch(setAddRemoveWatchlistItem({
                        watchlistItem: {
                            securityExpression: response,
                            security: undefined,
                            type: Watchlist_WatchlistItem_Type.EXPRESSION
                        },
                        selected: true,
                        watchlistId: currentSelectedWatchlist!.id,
                        showHighlighted: false
                    }))
                })
                if (rightSideWidget !== WidgetListItems.WATCHLIST) {
                    commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.WATCHLIST)
                }
                if (selectedWatchlistId !== currentSelectedWatchlist?.id) {
                    commonActions.dispatchWatchlistId(dispatch, currentSelectedWatchlist?.id)
                }
            }
            setOpenExpressionNameDialog(false)
        },
        [expressionSecurities, currentSelectedWatchlist, userWatchlists, convertedExpression, selectedWatchlistId, rightSideWidget, dispatch, currentSelectedWatchlist]
    )

    const onAddOrRemovalFromWatchlist = useCallback(() => {
        const savedExpression = getExpressionFromSaved(savedExpressions, convertedExpression)
        if(expressionMode && !hasAccess(FeatureCategory.WATCHLIST, '', FeatureName.COMMUNITY_WATCHLIST_ACCESS)){
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Security expressions",
                featureRestrictionCode:FeatureCode.SECURITY_EXPRESSIONS
            }))
            return
        }
        if (alreadyAddedToWatchlist || !expressionMode || (savedExpression && savedExpression!.name)) {
            if (rightSideWidget === null || rightSideWidget !== WidgetListItems.WATCHLIST) {
                commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.WATCHLIST)
            }
                dispatch(setAddRemoveWatchlistItem({
                        watchlistItem: alreadyAddedToWatchlist ? watchlistedItem! : {
                            type: expressionMode ? Watchlist_WatchlistItem_Type.EXPRESSION : Watchlist_WatchlistItem_Type.SECURITY,
                            security: !expressionMode && primarySecurity ? {
                                exchange: primarySecurity.exchange,
                                code: primarySecurity.code
                            } : undefined,
                    securityExpression: expressionMode ? savedExpression : undefined
                },
                selected: !alreadyAddedToWatchlist,
                watchlistId: currentSelectedWatchlist!.id,
                showHighlighted: alreadyAddedToWatchlist
            }))
        } else {
            setOpenExpressionNameDialog(true)
        }
    }, [rightSideWidget, expressionMode, currentSelectedWatchlist, userWatchlists, expressionSecurities, watchlistedItem, alreadyAddedToWatchlist, selectedWatchlistId, currentSelectedWatchlist, savedExpressions, convertedExpression, hasAccess])

    const onAddWatchlistSection = useCallback((name: string | null) => {
        const savedExpression = getExpressionFromSaved(savedExpressions, convertedExpression)
        if (alreadyAddedToWatchlist || !expressionMode || (savedExpression && savedExpression!.name)) {
            if (rightSideWidget === null || rightSideWidget !== WidgetListItems.WATCHLIST) {
                commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.WATCHLIST)
            }
            if (!name || !currentSelectedWatchlist) {
                setOpenSectionNameDialog(false)
                return
            }
            dispatch(setAddRemoveWatchlistItem({
                watchlistItem: alreadyAddedToWatchlist ? watchlistedItem! : {
                    type: Watchlist_WatchlistItem_Type.SECTION,
                    sectionName: name
                },
                selected: !alreadyAddedToWatchlist,
                watchlistId: currentSelectedWatchlist!.id,
                showHighlighted: alreadyAddedToWatchlist
            }))
        } else {
            setOpenExpressionNameDialog(true)
        }
    }, [rightSideWidget, expressionMode, currentSelectedWatchlist, userWatchlists, expressionSecurities, watchlistedItem, alreadyAddedToWatchlist, selectedWatchlistId, currentSelectedWatchlist, savedExpressions, convertedExpression])

    const shortcuts: ShortcutType = useMemo(() => ({
        [WatchlistShortcutsKeyName.addToWatchlist]: () => {
            AnalyticsManager.logEvent('Add To Watchlist', { source: 'keyboard' })
            addToWatchlist()
        }
    }), [addToWatchlist])

    useShortcuts(ShortcutsCategories.ADD_TO_WATCHLIST, shortcuts)

    return (
        <>
            {openExpressionNameDialog && <ExpressionNameDialog onClosed={onExpressionNameDialogClosed} />}
            {openSectionNameDialog && <SectionNameDialog open={openSectionNameDialog} onClose={onSectionNameDialogClosed} />}
        </>
    )
}

export default memo(WatchlistShortcutRegister)